import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PlusCircle } from "lucide-react";
import { adminPicks } from "@/lib/adminApi";
import { AdminPickRow } from "@/pages/admin/AdminPickRow";
import { AutoSettlePanel } from "@/pages/admin/AutoSettlePanel";

const VIEWS = {
  all: { title: "Manage Picks", eyebrow: "All Real Picks", empty: "No real picks yet." },
  pending: { title: "Pending Picks", eyebrow: "Published · Awaiting Result", empty: "No pending published picks. Publish a draft to see it here." },
  settled: { title: "Settled Picks", eyebrow: "Permanent Public Ledger", empty: "No settled picks yet. Settle a pending pick and it appears here and on the public Results page." },
};

export default function AdminPicks({ view: forced }) {
  const params = useParams();
  const view = forced || params.view || "all";
  const meta = VIEWS[view] || VIEWS.all;
  const { data, isLoading } = useQuery({
    queryKey: ["admin-picks", view],
    queryFn: () => adminPicks({ view: view === "all" ? undefined : view }),
  });

  return (
    <div data-testid={`admin-picks-${view}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">{meta.eyebrow}</p>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">{meta.title}</h1>
        </div>
        <Link to="/admin/picks/new" className="btn-primary !px-5 !py-2.5" data-testid="admin-picks-create-btn"><PlusCircle size={15} /> Create Pick</Link>
      </div>

      {view === "settled" && (
        <p className="mt-4 rounded-lg border border-[#1E2738] bg-[#0F131D] px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-500">
          Settled picks cannot be deleted or unpublished. Corrections are allowed but permanently logged in the audit trail.
        </p>
      )}

      <div className="mt-8 space-y-3">
        {view === "pending" && <AutoSettlePanel />}
        {isLoading && <p className="font-mono text-xs text-slate-500">Loading...</p>}
        {!isLoading && data?.picks.length === 0 && (
          <div className="panel px-6 py-16 text-center" data-testid="admin-picks-empty">
            <p className="text-sm text-slate-400">{meta.empty}</p>
          </div>
        )}
        {data?.picks.map((p) => <AdminPickRow key={p.id} pick={p} />)}
      </div>
    </div>
  );
}
